/**
 * Background version sync.
 *
 * The build embeds a unique version string (`__APP_VERSION__`) into both the
 * JS bundle and a `<meta name="app-version">` tag in index.html. Periodically
 * we re-fetch index.html (cache-busted) and compare. If the server's version
 * differs from what this client booted with, we clear caches/SW and reload
 * exactly once.
 *
 * Hard guards prevent the reload loop that bit us before:
 *   1. We only reload if server version != boot version AND it also !=
 *      the version we last attempted to reload to (sessionStorage).
 *   2. We never reload more than once every RELOAD_COOLDOWN_MS.
 */

export const CLIENT_APP_VERSION: string =
  typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "dev";

const LAST_RELOAD_VERSION_KEY = "workitect-version-reload-target";
const LAST_RELOAD_AT_KEY = "workitect-version-reload-at";
const RELOAD_COOLDOWN_MS = 60_000; // never auto-reload more than once per minute
const FOREGROUND_INTERVAL_MS = 2 * 60_000; // poll every 2 minutes when visible

let pollTimer: number | null = null;
let inFlight = false;
let started = false;
let serverVersion: string | null = null;
const listeners = new Set<(v: string | null) => void>();

function notify() {
  for (const fn of listeners) fn(serverVersion);
}

export function subscribeServerVersion(fn: (v: string | null) => void) {
  listeners.add(fn);
  fn(serverVersion);
  return () => {
    listeners.delete(fn);
  };
}

export function getServerVersion() {
  return serverVersion;
}

async function fetchServerVersion(): Promise<string | null> {
  try {
    const res = await fetch(`/?v=${Date.now()}`, {
      method: "GET",
      credentials: "omit",
      // No custom headers — keep the request "simple" so it never triggers
      // a CORS preflight on edge cases.
    });
    if (!res.ok) return null;
    const html = await res.text();
    const match = html.match(
      /<meta\s+name=["']app-version["']\s+content=["']([^"']+)["']/i,
    );
    return match?.[1] ?? null;
  } catch {
    return null;
  }
}

/**
 * Fetch the version meta tag from an arbitrary fully-qualified URL (e.g. the
 * published site) so the running tab can compare its bundle against another
 * deployment. Returns null on any error (network, CORS, missing tag).
 */
export async function fetchVersionFromUrl(url: string): Promise<string | null> {
  try {
    const sep = url.includes("?") ? "&" : "?";
    const res = await fetch(`${url}${sep}v=${Date.now()}`, {
      method: "GET",
      credentials: "omit",
      mode: "cors",
      cache: "no-store",
    });
    if (!res.ok) return null;
    const html = await res.text();
    const match = html.match(
      /<meta\s+name=["']app-version["']\s+content=["']([^"']+)["']/i,
    );
    return match?.[1] ?? null;
  } catch {
    return null;
  }
}

export async function clearAllCaches() {
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => false)));
    }
  } catch {
    /* noop */
  }
  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => false)));
    }
  } catch {
    /* noop */
  }
}

const LOGIN_PURGE_KEY = "workitect-login-cache-purged";

/**
 * Purge SW + Cache Storage on login, then verify the bundle is current.
 * If the server reports a newer build, the version check will hard-reload
 * once. Idempotent within a session — runs at most once per tab.
 */
export async function purgeCachesAndVerify() {
  if (sessionStorage.getItem(LOGIN_PURGE_KEY) === "1") {
    // Already purged this session — just re-check version.
    void checkVersionNow();
    return;
  }
  sessionStorage.setItem(LOGIN_PURGE_KEY, "1");
  await clearAllCaches();
  // After purging, verify version. If stale, this will hard-reload.
  await checkVersionNow();
}

function shouldReloadFor(version: string): boolean {
  // Don't loop: if we already attempted to reload to this exact version,
  // don't try again — the bundle either already matches or the server is lying.
  const lastTarget = sessionStorage.getItem(LAST_RELOAD_VERSION_KEY);
  if (lastTarget === version) return false;

  // Don't reload too often.
  const lastAtRaw = sessionStorage.getItem(LAST_RELOAD_AT_KEY);
  const lastAt = lastAtRaw ? Number(lastAtRaw) : 0;
  if (Number.isFinite(lastAt) && Date.now() - lastAt < RELOAD_COOLDOWN_MS) {
    return false;
  }
  return true;
}

async function performHardReload(targetVersion: string) {
  sessionStorage.setItem(LAST_RELOAD_VERSION_KEY, targetVersion);
  sessionStorage.setItem(LAST_RELOAD_AT_KEY, String(Date.now()));
  await clearAllCaches();
  // Cache-bust the navigation so the new index.html is fetched fresh.
  const url = new URL(window.location.href);
  url.searchParams.set("__v", targetVersion);
  window.location.replace(url.toString());
}

export async function checkVersionNow(options?: { reloadIfStale?: boolean }) {
  if (inFlight) return;
  inFlight = true;
  try {
    const v = await fetchServerVersion();
    if (!v) return;
    if (v !== serverVersion) {
      serverVersion = v;
      notify();
    }
    // If the server reports a version that doesn't match the bundle this
    // tab is running, schedule a single hard reload.
    if (
      options?.reloadIfStale !== false &&
      v !== CLIENT_APP_VERSION &&
      shouldReloadFor(v)
    ) {
      await performHardReload(v);
    } else if (v === CLIENT_APP_VERSION) {
      // We're up to date — clear any stale reload markers so future drift
      // can trigger a reload again.
      sessionStorage.removeItem(LAST_RELOAD_VERSION_KEY);
    }
  } finally {
    inFlight = false;
  }
}

function schedulePoll() {
  if (pollTimer !== null) return;
  pollTimer = window.setInterval(() => {
    if (document.visibilityState === "visible") {
      void checkVersionNow();
    }
  }, FOREGROUND_INTERVAL_MS);
}

export function startVersionSync() {
  if (started) return;
  started = true;

  // Initial check shortly after boot must never interrupt first paint/login.
  // Foreground polling can still reload later if a stale build is detected.
  window.setTimeout(() => void checkVersionNow({ reloadIfStale: false }), 4_000);

  // Poll while visible.
  schedulePoll();

  // Re-check when the tab regains focus or comes back online.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void checkVersionNow();
  });
  window.addEventListener("focus", () => void checkVersionNow());
  window.addEventListener("online", () => void checkVersionNow());
}
